<template>
  <div id="onboard" class="flex flex-col grow h-screen">
    <div class="w-full relative bg-tertiary h-fit">
      <div class="m-auto xl:max-w-[1184px] flex gap-2 items-center px-[20px] relative z-90">
        <div class="flex flex-row h-full items-center gap-4 md:gap-10">
          <router-link to="/">
            <img
              src="@/assets/img/otivo-logo-white-text.png"
              class="self-center max-w-[130px] py-8"
              alt="otivo-logo" />
          </router-link>
        </div>
      </div>
    </div>
    <div class="flex flex-col bg-blue-6 grow items-center">
      <div class="max-w-[560px] flex gap-8 flex-col mb-10">
        <OnboardForm ref="onboardForm" class="pb-0!" />
        <PrivacyTerms ref="privacyTerms" />
        <OtivoButton class="w-fit self-end" :loading="loading" @click="create">Submit</OtivoButton>
        <p v-if="formError" class="otivo-error-message mx-auto">{{ formError }}</p>
      </div>
    </div>
    <CustomFooter class="bg-tertiary text-blue-4 relative w-full md:pt-4 z-10" />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import CustomFooter from '@/components/Footer/CustomFooter.vue'
import PrivacyTerms from '@/components/FreemiumOnboarding/PrivacyTerms.vue'
import { useAccountCreationStore } from '@/store/pinia/AccountCreationStore.ts'
import api from '@/services/Api.ts'
import OtivoButton from '@/components/OtivoButton.vue'
import OnboardForm from '@/components/FreemiumOnboarding/OnboardForm.vue'
import { useSessionStore } from '@/store/pinia/SessionStore.ts'
import { useAuth0 } from '@/lib/AuthenticatorPlugin.ts'
import { generateOtivoAccountNumber } from '@/composables/getSuperFundNames.ts'

const accountCreationStore = useAccountCreationStore()
const sessionStore = useSessionStore()
const auth0 = useAuth0()

const whitelabel = sessionStore.getWhitelabelData
const onboardForm = ref<{ validate: () => { dob: string; firstName: string; lastName: string } }>()
const privacyTerms = ref<{ consented: boolean; acknowledgedPrivacy: boolean }>()

const loading = ref(false)
const formError = ref()
const create = async () => {
  formError.value = undefined
  const formData = onboardForm.value?.validate()
  if (!formData || !privacyTerms.value?.consented || !privacyTerms.value.acknowledgedPrivacy) return

  loading.value = true
  try {
    const res = await api().Post('/v3/onboarding/onboard', {
      data: {
        dob: formData.dob,
        first_name: formData.firstName,
        last_name: formData.lastName,
        balance: accountCreationStore.getSuperBalance,
        super_account_id: generateOtivoAccountNumber(),
      },
      consented: privacyTerms.value.consented,
      super_product_id: accountCreationStore.getSuperProductId,
      whitelabel_id: whitelabel.id,
    })
    if (res.data.key) {
      sessionStorage.setItem('otivo_signup_started', Date.now().toString())
      sessionStorage.setItem('create_key', res.data.key)
      auth0.createAccount()
    } else throw new Error('No key returned')
  } catch (e) {
    formError.value = 'An error occurred. Please try again.'
    console.error(e)
  } finally {
    loading.value = false
  }
}
</script>

<style scoped></style>
